import { useState, useEffect, useRef, ReactNode, SetStateAction } from "react";

/**
 * Configuration options for the useNavbar hook.
 *
 * @interface UseNavbarOptions
 * @property {boolean} autoHide - Whether to auto-hide the navbar on scroll (default: true)
 * @property {number} autoShowTimeout - Time in milliseconds after which hidden navbar reappears (default: 3000)
 */
interface UseNavbarOptions {
  autoHide?: boolean;
  autoShowTimeout?: number;
}

/**
 * Return interface for the useNavbar hook.
 *
 * @interface UseNavbarReturn
 * @property {boolean} isVisible - Whether the navbar is currently visible
 * @property {boolean} isTimerTriggered - Whether visibility was triggered by a timer (affects transparency)
 * @property {function} setIsTimerTriggered - Function to update the timer trigger state
 * @property {function} handleMouseEnter - Function to handle mouse enter events on the navbar
 * @property {function} handleMouseLeave - Function to handle mouse leave events, requires menu state
 * @property {function} handleClick - Function to handle click events on the navbar
 * @property {function} getNavbarClasses - Function to generate appropriate class names based on state
 */
interface UseNavbarReturn {
  isVisible: boolean;
  isTimerTriggered: boolean;
  buttons: ReactNode[];
  setButtons: React.Dispatch<SetStateAction<ReactNode[]>>;
  setIsTimerTriggered: React.Dispatch<React.SetStateAction<boolean>>;
  handleMouseEnter: () => void;
  handleMouseLeave: (isMenuOpen: boolean) => void;
  handleClick: () => void;
  getNavbarClasses: (
    baseClass: string,
    visibleClass: string,
    hiddenClass: string,
    transparentClass: string
  ) => string;
}

/**
 * A reusable navbar hook that provides auto-hide functionality on scroll with smooth animations.
 * Features include:
 * - Auto-hide on scroll down
 * - Immediate reveal on scroll up
 * - Timed reappearance after hiding
 * - Transparent mode when timer-triggered
 * - Interactive opacity control based on user interaction
 *
 * @param {UseNavbarOptions} options - Configuration options for navbar behavior
 * @param {boolean} options.autoHide - Whether to auto-hide on scroll (default: true)
 * @param {number} options.autoShowTimeout - Milliseconds until navbar reappears (default: 3000)
 *
 * @returns {UseNavbarReturn} Object containing navbar state and control functions
 *
 * @example
 * // Basic usage with default settings
 * const {
 *   handleMouseEnter,
 *   handleMouseLeave,
 *   handleClick,
 *   getNavbarClasses
 * } = useNavbar();
 *
 * @example
 * // Custom configuration
 * const {
 *   isTimerTriggered,
 *   setIsTimerTriggered,
 *   handleMouseEnter,
 *   handleMouseLeave,
 *   handleClick,
 *   getNavbarClasses
 * } = useNavbar({
 *   autoHide: true,
 *   autoShowTimeout: 5000 // 5 seconds
 * });
 *
 * // Generate class names for the navbar
 * const navbarClasses = getNavbarClasses(
 *   styles.navbar,
 *   styles.navbarVisible,
 *   styles.navbarHidden,
 *   styles.navbarTransparent
 * );
 */
export const useNavbar = ({
  autoHide = true,
  autoShowTimeout = 3000,
}: UseNavbarOptions = {}): UseNavbarReturn => {
  // States
  const [isVisible, setIsVisible] = useState(true);
  const [isTimerTriggered, setIsTimerTriggered] = useState(false);
  const [buttons, setButtons] = useState<ReactNode[]>([]);

  // Refs to avoid dependency issues
  const isVisibleRef = useRef(true);
  const lastScrollY = useRef(0);
  const timer = useRef<number | null>(null);
  const isScrollingDown = useRef(false);
  const throttleTimer = useRef<number | null>(null);
  const lastScrollTime = useRef(0);

  // Keep the ref in sync with state
  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  // Scroll handling effect
  useEffect(() => {
    if (!autoHide) {
      // If auto-hide is disabled, always show the navbar
      setIsVisible(true);
      return () => {}; // No cleanup needed
    }

    const handleScrollThrottled = () => {
      const now = Date.now();
      // Throttle to once per 100ms
      if (now - lastScrollTime.current >= 100) {
        const currentScrollY = window.scrollY;

        if (currentScrollY < lastScrollY.current) {
          // Scrolling up
          isScrollingDown.current = false;
          setIsVisible(true);
          setIsTimerTriggered(false);
          if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
          }
        } else if (
          currentScrollY > 50 &&
          isVisibleRef.current &&
          !isScrollingDown.current
        ) {
          // Scrolling down - first time detected
          isScrollingDown.current = true;
          setIsVisible(false);

          if (timer.current) {
            clearTimeout(timer.current);
          }

          timer.current = setTimeout(() => {
            setIsVisible(true);
            setIsTimerTriggered(true);
          }, autoShowTimeout);
        }

        lastScrollY.current = currentScrollY;
        lastScrollTime.current = now;

        // Clear any pending throttle
        if (throttleTimer.current !== null) {
          window.clearTimeout(throttleTimer.current);
          throttleTimer.current = null;
        }
      } else if (throttleTimer.current === null) {
        // Schedule a throttled execution
        throttleTimer.current = window.setTimeout(
          () => {
            handleScrollThrottled();
            throttleTimer.current = null;
          },
          100 - (now - lastScrollTime.current)
        );
      }
    };

    window.addEventListener("scroll", handleScrollThrottled);

    return () => {
      window.removeEventListener("scroll", handleScrollThrottled);
      if (timer.current) {
        clearTimeout(timer.current);
      }
      if (throttleTimer.current !== null) {
        window.clearTimeout(throttleTimer.current);
      }
    };
  }, [autoHide, autoShowTimeout]);

  // Event handlers
  const handleMouseEnter = () => {
    if (isTimerTriggered) {
      setIsTimerTriggered(false);
    }
  };

  const handleMouseLeave = (isMenuOpen: boolean) => {
    if (isVisible && !isMenuOpen && isTimerTriggered) {
      setIsTimerTriggered(true);
    }
  };

  const handleClick = () => {
    if (isTimerTriggered) {
      setIsTimerTriggered(false);
    }
  };

  // Helper to generate class names
  const getNavbarClasses = (
    baseClass: string,
    visibleClass: string,
    hiddenClass: string,
    transparentClass: string
  ) => {
    return `${baseClass} ${
      isVisible ? visibleClass : hiddenClass
    } ${isTimerTriggered ? transparentClass : ""}`;
  };

  return {
    isVisible,
    isTimerTriggered,
    buttons,
    setIsTimerTriggered,
    setButtons,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    getNavbarClasses,
  };
};
